import { Button, ButtonProps, Grid } from '@purpur/library';
import styled from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurMotion,
    getPurpurSpacing,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

export const CarouselWrapper = styled.div.attrs<{ $isVariableWidth: boolean }>(({ $isVariableWidth }) => ({
    className: 'embla',
    ...($isVariableWidth ? { as: Grid, noGap: true } : {}),
}))``;

export const CarouselViewport = styled.div.attrs({ className: 'embla__viewport' })`
    overflow: hidden;
`;

export const CarouselContainer = styled.ul.attrs<{
    $isVariableWidth: boolean;
}>({
    className: 'embla__container',
})`
    display: flex;
    margin: 0;
    padding: 0;
    touch-action: pan-y pinch-zoom;

    ${({ $isVariableWidth }) =>
        $isVariableWidth &&
        `margin-left: calc(-1 * ${getPurpurSpacing('200')});
    `}

    ${({ $isVariableWidth }) =>
        $isVariableWidth &&
        purpurMedia('md')`
            margin-left: calc(-1 * ${getPurpurSpacing('300')});
        `};
`;

export const CarouselSlide = styled.li.attrs<{
    $isVariableWidth: boolean;
    $controlsHiddenXlg: boolean;
    $controlsHiddenLg: boolean;
    $controlsHiddenMd: boolean;
    $controlsHiddenSm: boolean;
}>({ className: 'embla__slide' })`
    flex: 0 0 100%;
    min-width: 0;
    list-style: none;
    display: block;
    transform: translate3d(0, 0, 0);

    ${({ $isVariableWidth, $controlsHiddenSm }) =>
        $isVariableWidth &&
        `
        padding-left: ${getPurpurSpacing('200')};
        margin-top: ${getPurpurSpacing('50')};
        margin-bottom: ${getPurpurSpacing('50')};
        ${$controlsHiddenSm ? `flex: 0 0 calc(100%  - ${getPurpurSpacing('100')});` : `flex: 0 0 90%;`}
    
        &:last-of-type {
            margin-right: ${getPurpurSpacing('50')};
        }
    
        &:first-of-type {
            margin-left: ${getPurpurSpacing('50')};
        }
    `}

    ${({ $isVariableWidth, $controlsHiddenMd }) =>
        $isVariableWidth &&
        purpurMedia('md')`
            ${$controlsHiddenMd ? `flex: 0 0 calc((100%  - ${getPurpurSpacing('100')}) / 2);` : `flex: 0 0 47%;`}
            padding-left: ${getPurpurSpacing('300')};
        `};

    ${({ $isVariableWidth, $controlsHiddenLg }) =>
        $isVariableWidth &&
        purpurMedia('lg')`
            ${$controlsHiddenLg ? `flex: 0 0 calc((100%  - ${getPurpurSpacing('100')}) / 3);` : `flex: 0 0 30%;`}
        `};

    ${({ $isVariableWidth, $controlsHiddenXlg }) =>
        $isVariableWidth &&
        purpurMedia('xl')`
            ${$controlsHiddenXlg ? `flex: 0 0 calc((100%  - ${getPurpurSpacing('100')}) / 4);` : `flex: 0 0 24%;`}
        `};
    > * {
        height: 100%;
    }
`;

export const CarouselControls = styled(Grid).attrs<{
    $controlsHiddenXlg: boolean;
    $controlsHiddenLg: boolean;
    $controlsHiddenMd: boolean;
    $controlsHiddenSm: boolean;
    $noControlsMargin?: boolean;
}>({
    className: 'embla__controls',
    noGap: true,
})`
    display: grid;
    grid-template-areas:
        'progress progress'
        '. buttons';
    justify-content: space-between;
    gap: ${getPurpurSpacing('200')};
    margin-top: ${({ $noControlsMargin }) => ($noControlsMargin ? 0 : getPurpurSpacing('200'))};

    ${({ $controlsHiddenXlg }) => $controlsHiddenXlg && purpurMedia('xl')`display: none;`}
    ${({ $controlsHiddenLg }) => $controlsHiddenLg && purpurMedia('lg')`display: none;`}
    ${({ $controlsHiddenMd }) => $controlsHiddenMd && purpurMedia('md')`display: none;`}
    ${({ $controlsHiddenSm }) => $controlsHiddenSm && purpurMedia('sm')`display: none;`}
`;

export const CarouselButtons = styled.div.attrs({ className: 'embla__buttons' })`
    grid-area: buttons;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${getPurpurSpacing('150')};
    align-items: center;
    margin-right: ${getPurpurSpacing('50')};
`;

export const PrevButton = styled(Button).attrs<Partial<ButtonProps>>({
    type: 'button',
    className: 'embla__button embla__button--prev',
    iconOnly: true,
    variant: 'primary',
})``;

export const NextButton = styled(Button).attrs<Partial<ButtonProps>>({
    type: 'button',
    className: 'embla__button embla__button--next',
    iconOnly: true,
    variant: 'primary',
})``;

export const Progress = styled.div.attrs({ className: 'embla__progress' })`
    grid-area: progress;
    border-radius: ${getPurpurBorder('radiusXs')};
    background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
    position: relative;
    height: 0.25rem;
    width: 100%;
    overflow: hidden;
`;

export const ProgressBar = styled.div.attrs({ className: 'embla__progress__bar' })`
    background-color: ${getPurpurColor('backgroundInteractivePrimary')};
    position: absolute;
    width: 100%;
    top: 0;
    bottom: 0;
    left: -100%;
    transition: transform ${getPurpurMotion('duration500')} ${getPurpurMotion('easingEaseOut')};
`;
